import { Address, Establishment } from './establishment';
import { BENEFIT_TYPE, PSPBenefit } from './medicine';
import { DateTime } from 'luxon';
import { Prescriber } from './users';
import { onlyDigits } from '../helpers/string';
import { findSpecialtyByRqes } from '../helpers/rqeSpecialties';

// Mesmo padrão do PSP (getPspBenefit): benefício do tipo INDUSTRIA identificado pelo prefixo da
// Descricao — 'PSP' pro PSP, 'AMOSTRA_GRATIS' pra amostra grátis.
export const isFreeSampleBenefit = (benefit: PSPBenefit): boolean =>
  benefit.Tipo === BENEFIT_TYPE.INDUSTRIA && !!benefit.Descricao?.startsWith?.('AMOSTRA_GRATIS');

export function getFreeSampleBenefit(beneficios?: PSPBenefit[]): PSPBenefit | undefined {
  return beneficios?.find(isFreeSampleBenefit);
}

// Entrada vinda do fluxo de edição de medicamento (checkbox "Gostaria de receber uma amostra
// grátis"): a marca e os SKUs do catálogo que correspondem ao medicamento prescrito (casados por
// EAN, ver findCatalogMatchByEan). O sidebar abre direto na marca: se o médico for elegível, com
// esses SKUs marcados; se não for, no modo "Tenho interesse". `eans` (os EANs dos SKUs casados) e
// `medicineId` (IdMedicamento do medicamento prescrito) vão só pros eventos de tracking.
export interface FreeSampleContext {
  brand: string;
  skus?: string[];
  eans?: string[];
  medicineId?: number;
}

// Casa o medicamento da busca com o catálogo do parceiro pelo EAN (a busca traz a lista de EANs
// do medicamento; o catálogo, o `eanCode` de cada SKU). Elegível ou não, o médico pode seguir: o
// elegível solicita a amostra, o não elegível registra interesse (ELIGIBILITY_INCLUSION_REQUEST —
// não exige cadastro no parceiro). Sem match = medicamento fora de qualquer programa ativo.
export function findCatalogMatchByEan(catalog: FreeSampleMedicine[], eans: string[]): FreeSampleContext | null {
  const medicineEans = new Set(eans.map(onlyDigits).filter(Boolean));
  const matches = catalog.filter((item) => medicineEans.has(onlyDigits(item.eanCode ?? '')));

  if (matches.length === 0) return null;

  const brand = matches[0].brand;
  const brandMatches = matches.filter((item) => item.brand === brand);
  return { brand, skus: brandMatches.map((item) => item.sku), eans: brandMatches.map((item) => item.eanCode) };
}

// Espelha (o subconjunto que o frontend usa de) FreeSampleResponseDto do backend — a campanha
// ativa, descoberta via GET /product/free-samples antes de chamar eligibility/request.
export interface FreeSampleProgram {
  id: number;
  optoutUrl?: string;
  partnerProgramUrl?: string;
  pharmaceuticalPageUrl?: string;
  pharmaceuticalPhone?: string;
}

// Espelha FreeSampleTransactionTypeEnum do backend (backoffice-ventures-api,
// modules/product/free-sample/application/enums/free-sample.enum.ts).
export type FreeSampleTransactionType =
  | 'SAMPLE_REQUEST_CONFIRMED'
  | 'SAMPLE_REQUEST_UNDER_REVIEW'
  | 'ELIGIBILITY_INCLUSION_REQUEST';

// Espelha CheckEligibilityOutput.medicines do backend — catálogo plano de marcas, visível a todos
// os médicos, com elegibilidade por item. Sem estoque/quantidade: `sku` é só informativo.
export interface FreeSampleMedicine {
  brand: string;
  sku: string;
  eanCode: string;
  medicineName: string;
  eligible: boolean;
  subjectToAnalysis?: boolean;
}

// Agrupamento client-side do catálogo plano por marca, pra alimentar a tela de catálogo (lista de
// marcas) e o painel de detalhe de cada marca. Os itens de uma mesma marca sempre compartilham o
// mesmo `eligible` (a elegibilidade é checada por médico+marca no backend, não por SKU).
export interface FreeSampleCatalogBrand {
  brand: string;
  eligible: boolean;
  subjectToAnalysis: boolean;
  items: FreeSampleMedicine[];
}

export function groupCatalogByBrand(medicines: FreeSampleMedicine[]): FreeSampleCatalogBrand[] {
  const order: string[] = [];
  const byBrand = new Map<string, FreeSampleMedicine[]>();

  medicines.forEach((medicine) => {
    if (!byBrand.has(medicine.brand)) {
      order.push(medicine.brand);
      byBrand.set(medicine.brand, []);
    }
    byBrand.get(medicine.brand)!.push(medicine);
  });

  return order.map((brand) => {
    const items = byBrand.get(brand)!;
    return {
      brand,
      eligible: items.some((item) => item.eligible),
      subjectToAnalysis: items.some((item) => item.subjectToAnalysis),
      items,
    };
  });
}

export interface FreeSampleDeliveryAddress {
  postalCode: string;
  address: string;
  number: string;
  withoutNumber: boolean;
  complement: string;
  neighborhood: string;
  city: string;
  // Usado só na tela (autopreenchido pela busca de CEP) — o endpoint de solicitação do backend não
  // tem campo de UF do endereço de entrega (CEP + cidade já bastam, CEP não é ambíguo entre
  // estados), então este campo não é enviado no payload da API.
  state: string;
}

// Local de atendimento selecionável no step de endereço. Hoje só existe 1 opção por médico
// (`Establishment` da sessão atual) — a estrutura já suporta múltiplas, mas uma fonte real de
// múltiplos locais de atendimento por médico ainda não existe no frontend nem no backend.
export interface FreeSampleEstablishmentOption {
  id: number;
  label: string;
  address: FreeSampleDeliveryAddress;
}

// Dados do médico usados no fluxo de amostra grátis, obtidos do cadastro dele (via
// usePrescriptionStore().prescriber()) e pré-preenchidos nas telas — o médico autoriza o
// compartilhamento (consentimento) e, no fluxo de interesse, pode editar e-mail/telefone antes de
// enviar (ver FreeSampleInterestStep).
// `Prescriber` (src/interfaces/users.ts) não tem telefone nem data de nascimento, mas o backend
// exige os dois. Por isso o fluxo tem uma tela dedicada (FreeSamplePrescriberStep) que pré-preenche o
// que existir no cadastro e pede o resto ao médico. A especialidade é o nome que bate com o `idRqe`
// do médico na tabela de-para (helpers/rqeSpecialties.ts) — o backend recebe o nome, nunca o id. Sem
// idRqe no payload, o sidebar busca os RQEs na API (FreeSampleService.getSpecialtyFromRqes) e, se
// ainda assim não achar, pré-preenche com "SEM ESPECIALIDADE". Em todos os casos o médico pode trocar
// por outra opção da lista no formulário.
export interface FreeSamplePrescriberInfo {
  // Só usado pra buscar os RQEs do médico na API quando a especialidade vem vazia — não vai no payload.
  prescriberId?: number;
  name: string;
  cpf: string;
  crmNumber: string;
  crmState: string;
  specialty: string;
  email: string;
  phone?: string;
  birthdate?: string;
}

// Espelha RequestSampleItemDto do backend.
export interface FreeSampleRequestItem {
  brand: string;
  sku?: string; // ausente = tipo 3 (ELIGIBILITY_INCLUSION_REQUEST, marca em que o médico não é elegível)
}

// Espelha RequestSampleRequestDto do backend (POST /product/free-samples/request), já achatado
// pros campos que vêm do médico/endereço — a montagem final do body HTTP acontece no service.
export interface FreeSampleRequestPayload {
  freeSampleId: number;
  idempotencyKey: string;
  items: FreeSampleRequestItem[];
  prescriber: FreeSamplePrescriberInfo;
  deliveryAddress: FreeSampleDeliveryAddress;
  // `shippingConsent` (obrigatório) mapeia pro único `optIn` do RequestSampleRequestDto —
  // é a autorização de compartilhar dados com o laboratório. `communicationsConsent` (opcional,
  // "quero receber avisos") não tem campo correspondente no backend ainda — fica só no frontend,
  // não é enviado no payload HTTP (ver freeSampleService.ts).
  shippingConsent: boolean;
  communicationsConsent: boolean;
}

// Espelha RequestSampleItemResultDto do backend. `partnerOrderId` é campo interno de
// integração e fica de fora de propósito — o presenter do backend (free-sample.presenter.ts)
// omite explicitamente esse campo da resposta pública.
export interface FreeSampleRequestResultItem {
  brand: string;
  sku?: string;
  transactionType?: FreeSampleTransactionType;
  isSuccess: boolean;
  // Motivo da falha quando isSuccess=false. Texto técnico — nunca exibido direto ao médico (ver
  // FREE_SAMPLE_KNOWN_REQUEST_ERRORS).
  message?: string;
}

// Espelha RequestSampleResponseDto do backend.
export interface FreeSampleRequestOutput {
  results: FreeSampleRequestResultItem[];
}

export interface FreeSampleErrorContent {
  title: string;
  description: string;
}

// Falhas por item do /request que o médico entende e em que pode agir. A chave é a `message`
// exata que o backend devolve no item (contrato combinado com o backend: o texto é fixo e não muda
// sem aviso). Qualquer outra mensagem cai na tela de erro genérica. Ao surgir um caso novo, é só
// incluir aqui.
export const FREE_SAMPLE_KNOWN_REQUEST_ERRORS: Record<string, FreeSampleErrorContent> = {
  'Limite individual atingido para este médico': {
    title: 'Você já solicitou esta amostra',
    description:
      'Existe um limite de solicitações por médico para cada produto. Uma nova solicitação ficará disponível no próximo período.',
  },
};

export function getKnownRequestError(results: FreeSampleRequestResultItem[]): FreeSampleErrorContent | undefined {
  const known = results.find(
    (item) =>
      !item.isSuccess &&
      !!item.message &&
      Object.prototype.hasOwnProperty.call(FREE_SAMPLE_KNOWN_REQUEST_ERRORS, item.message)
  );

  return known?.message ? FREE_SAMPLE_KNOWN_REQUEST_ERRORS[known.message] : undefined;
}

// Fonte única dos dados do médico exibidos no fluxo de amostra grátis — evita repetir esse
// mapeamento em cada ponto de entrada (Prescription.tsx, HamburgerMenuPanel.tsx, PrescriberCentral.tsx).
export function mapPrescriberInfo(prescriber?: Prescriber | null): FreeSamplePrescriberInfo {
  return {
    prescriberId: prescriber?.idPrescritor,
    name: prescriber?.Nome ?? '',
    cpf: prescriber?.Documento ?? '',
    crmNumber: prescriber?.Numero ?? '',
    crmState: prescriber?.UFConselho ?? '',
    specialty: findSpecialtyByRqes(prescriber?.RQEs),
    email: prescriber?.Email ?? '',
    // Sem fonte no cadastro — o médico preenche em FreeSamplePrescriberStep.
    phone: undefined,
    birthdate: undefined,
  };
}

// Dados do médico editáveis na tela FreeSamplePrescriberStep, no formato de exibição dos inputs
// (telefone mascarado, nascimento em dd/MM/yyyy). `specialty` vem pré-preenchida pelo idRqe (ver
// FreeSamplePrescriberInfo) e o médico pode trocar por outra opção da lista — é obrigatória no envio.
export interface FreeSamplePrescriberForm {
  email: string;
  phone: string;
  birthdate: string;
  specialty: string;
}

export function toPrescriberForm(info: FreeSamplePrescriberInfo): FreeSamplePrescriberForm {
  const birthdate = info.birthdate ? DateTime.fromISO(info.birthdate) : null;

  return {
    email: info.email,
    phone: info.phone ?? '',
    birthdate: birthdate?.isValid ? birthdate.toFormat('dd/MM/yyyy') : '',
    specialty: info.specialty,
  };
}

// Converte o formulário pro formato que o backend valida: telefone só com dígitos (MaxLength(20)) e
// nascimento em ISO YYYY-MM-DD (IsDateString) — ver RequestSampleRequestDto.
export function applyPrescriberForm(
  info: FreeSamplePrescriberInfo,
  form: FreeSamplePrescriberForm
): FreeSamplePrescriberInfo {
  const birthdate = DateTime.fromFormat(form.birthdate, 'dd/MM/yyyy');

  return {
    ...info,
    specialty: form.specialty,
    email: form.email.trim(),
    phone: onlyDigits(form.phone) || undefined,
    birthdate: birthdate.isValid ? (birthdate.toISODate() ?? undefined) : undefined,
  };
}

export function mapEstablishmentAddress(address?: Address | null): FreeSampleDeliveryAddress | undefined {
  if (!address) return undefined;

  return {
    postalCode: address.CodigoPostal ?? '',
    address: address.Endereco1 ?? '',
    number: address.Numero ?? '',
    withoutNumber: false,
    complement: address.Endereco2 ?? '',
    neighborhood: address.Bairro ?? '',
    city: address.Cidade ?? '',
    state: address.Estado ?? '',
  };
}

// Hoje só existe 1 local de atendimento por médico (o estabelecimento da sessão atual) — ver
// FreeSampleEstablishmentOption sobre a limitação de múltiplos locais.
export function mapEstablishmentOptions(establishment?: Establishment | null): FreeSampleEstablishmentOption[] {
  const address = mapEstablishmentAddress(establishment?.Endereco);
  if (!establishment?.Nome || !address) return [];

  return [{ id: establishment.idEstabelecimento, label: establishment.Nome, address }];
}
